<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="row">
      <div class="col-12">
        <!-- Creating from an establishment page means that establishment is the one
             ordering: it is the SATELLITE (destination). The counterpart to pick is
             its production establishment (source). -->
        <div class="alert alert-secondary">
          <i class="fas fa-info-circle mr-1"></i>
          {{
            $t("ESTABLISHMENT_INTERNAL_ORDERS.ADD_FROM_ESTABLISHMENT_HELP", {
              establishment: establishment.company_name,
            })
          }}
        </div>
      </div>

      <div class="col-12" v-if="satelliteWarning">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_SATELLITE_CAFETERIA_WARNING") }}
        </div>
      </div>

      <div class="col-12">
        <base-input
          :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')} *`"
        >
          <production-establishment-selector
            :onlyActive="true"
            :establishment="sourceEstablishmentId"
            :filterOrganization="establishment.organization?.id"
            @establishmentChanged="onSourceChanged"
            @loaded="onProductionEstablishmentsLoaded"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.sourceEstablishment" />
        <div class="alert alert-warning" v-if="noProductionEstablishment">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PRODUCTION_ESTABLISHMENT_WARNING") }}
        </div>
        <p class="text-muted small" v-if="productionCafeteriaName">
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_CAFETERIA") }} :
          {{ productionCafeteriaName }}
        </p>
      </div>

      <div class="col-12">
        <base-input
          :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK_STARTS_AT')} *`"
        >
          <flat-picker
            v-model="weekStartsAt"
            :config="{
              allowInput: true,
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
            @on-change="alignWeek"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.week_starts_at" />
        <p class="text-muted small" v-if="weekEndsAt">
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK") }} :
          {{ weekStartsAt }} &rarr; {{ weekEndsAt }}
        </p>
      </div>

      <div class="col-12">
        <base-input :label="$t('COMMON.NOTE')" :placeholder="$t('COMMON.NOTE')">
          <textarea class="form-control" rows="3" v-model="excerpt"></textarea>
        </base-input>
        <validation-error :errors="apiValidationErrors.excerpt" />
      </div>
    </div>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="createOrder"
        :disabled="loading || !sourceEstablishmentId"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("COMMON.SAVE") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import { cloneDeep } from "lodash";
import moment from "moment";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import ProductionEstablishmentSelector from "@/components/ProductionEstablishmentSelector.vue";
import {
  establishmentCanOrder,
  findSatelliteCafeteriaWithProduction,
} from "@/helpers/internalOrderEligibility";
import defaultOrder from "../../../InventoryModule/EstablishmentInternalOrderManagement/defaultEstablishmentInternalOrder";

export default {
  name: "EstablishmentViewInternalOrdersAddInternalOrderModal",
  components: {
    ProductionEstablishmentSelector,
    ValidationError,
    flatPicker,
  },
  mixins: [formMixin],
  props: {
    establishment: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      sourceEstablishmentId: null,
      sourceCafeteriaId: null,
      destinationCafeteriaId: null,
      productionCafeteriaName: null,
      weekStartsAt: this.nextMonday(),
      weekEndsAt: null,
      excerpt: "",
      satelliteWarning: false,
      noProductionEstablishment: false,
    };
  },
  watch: {
    showModal(value) {
      if (value) {
        // Recompute the target week on every opening, so a tab left open for days
        // does not keep proposing a stale week.
        this.weekStartsAt = this.nextMonday();
        this.alignWeek();
        this.checkSatelliteEligibility();
        this.resolveProductionEstablishment();
      } else {
        this.resetForm();
      }
    },
    establishment: {
      deep: true,
      handler() {
        this.resetForm();
      },
    },
  },
  created() {
    this.alignWeek();
  },
  methods: {
    nextMonday() {
      return moment().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD");
    },
    /**
     * The ordered week always runs monday to friday: snap whatever was picked.
     */
    alignWeek() {
      if (!this.weekStartsAt) {
        this.weekEndsAt = null;
        return;
      }
      const monday = moment(this.weekStartsAt).startOf("isoWeek");
      this.weekStartsAt = monday.format("YYYY-MM-DD");
      this.weekEndsAt = monday.clone().add(4, "days").format("YYYY-MM-DD");
    },
    /**
     * Ordering requires a SATELLITE cafeteria in this establishment. Warning only —
     * creation stays possible, and the API carries no check for it.
     */
    async checkSatelliteEligibility() {
      const canOrder = await establishmentCanOrder(this.establishment?.id);
      this.satelliteWarning = canOrder === false;
    },
    onProductionEstablishmentsLoaded(establishments) {
      this.noProductionEstablishment = (establishments || []).length === 0;
    },
    onSourceChanged(establishmentId) {
      this.sourceEstablishmentId = establishmentId;
      this.resetApiValidation();
    },
    /**
     * A satellite cafeteria declares its production cafeteria: use it to prefill
     * the production establishment instead of asking the user to know it.
     */
    async resolveProductionEstablishment() {
      this.productionCafeteriaName = null;
      if (!this.establishment?.id) return;

      const satellite = await findSatelliteCafeteriaWithProduction(this.establishment.id);
      if (!satellite) return;

      this.destinationCafeteriaId = satellite.id;
      this.sourceCafeteriaId = satellite.productionCafeteria.id;
      this.productionCafeteriaName = satellite.productionCafeteria.name;

      const productionEstablishment = satellite.productionCafeteria.establishment;
      if (productionEstablishment && !this.sourceEstablishmentId) {
        this.sourceEstablishmentId = productionEstablishment.id;
      }
    },
    resetForm() {
      this.loading = false;
      this.sourceEstablishmentId = null;
      this.sourceCafeteriaId = null;
      this.destinationCafeteriaId = null;
      this.productionCafeteriaName = null;
      this.satelliteWarning = false;
      this.noProductionEstablishment = false;
      this.weekStartsAt = this.nextMonday();
      this.alignWeek();
      this.excerpt = "";
      this.resetApiValidation();
    },
    closeModal() {
      this.resetForm();
      this.$emit("onCloseModal");
    },
    async createOrder() {
      if (!this.establishment?.id || !this.sourceEstablishmentId) {
        return;
      }
      this.alignWeek();
      this.loading = true;

      const payload = cloneDeep(defaultOrder);
      payload.week_starts_at = this.weekStartsAt;
      payload.week_ends_at = this.weekEndsAt;
      payload.excerpt = this.excerpt;
      payload.organization = {
        type: "organizations",
        id: this.establishment.organization?.id,
      };
      payload.sourceEstablishment = {
        type: "establishments",
        id: this.sourceEstablishmentId,
      };
      payload.destinationEstablishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      if (this.sourceCafeteriaId) {
        payload.sourceCafeteria = { type: "cafeterias", id: this.sourceCafeteriaId };
      } else {
        delete payload.sourceCafeteria;
      }
      if (this.destinationCafeteriaId) {
        payload.destinationCafeteria = {
          type: "cafeterias",
          id: this.destinationCafeteriaId,
        };
      } else {
        delete payload.destinationCafeteria;
      }
      if (this.establishment.serviceCenter?.id) {
        payload.serviceCenter = {
          type: "service-centers",
          id: this.establishment.serviceCenter.id,
        };
      }
      payload.relationshipNames = payload.relationshipNames.filter(
        (name) => payload[name] !== undefined
      );

      try {
        await this.$store.dispatch("establishmentInternalOrders/add", { data: payload });
        const order = this.$store.getters["establishmentInternalOrders/order"];
        this.$notify({
          type: "success",
          message: this.$t(
            "ESTABLISHMENT_INTERNAL_ORDERS.ESTABLISHMENT_INTERNAL_ORDER_ADDED"
          ),
        });
        this.$emit("created", order);
        this.closeModal();
        this.$router.push({
          name: "View EstablishmentInternalOrder",
          params: { id: order.id },
        });
      } catch (error) {
        if (error?.response?.data?.errors) {
          this.setApiValidation(error.response.data.errors);
        }
        this.$notify({
          type: "danger",
          message:
            error?.response?.data?.errors?.[0]?.detail ||
            error?.response?.data?.errors?.[0]?.title ||
            this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
